import type { BrowserSession, TerrainPayload, VillageSummary } from '../gateway/contracts';
import type { LegacyLoginRejection } from './LegacyLoginRejection';

export type AppState =
  | Readonly<{ phase: 'starting' }>
  | Readonly<{
      phase: 'anonymous';
      username?: string;
      password?: string;
      message?: string;
      loginRejection?: LegacyLoginRejection;
    }>
  | Readonly<{ phase: 'authenticating'; username: string }>
  | Readonly<{ phase: 'loading-world'; session: BrowserSession; village: VillageSummary }>
  | Readonly<{
      phase: 'world';
      session: BrowserSession;
      village: VillageSummary;
      terrain: TerrainPayload;
      assetRecoveryNonce?: number;
    }>
  | Readonly<{
      phase: 'world-load-failed';
      session: BrowserSession;
      village: VillageSummary;
      terrain: TerrainPayload;
      message: string;
      assetRecoveryNonce: number;
      moduleLoadFailed?: boolean;
    }>
  | Readonly<{ phase: 'fatal'; message: string }>;

export type AppEvent =
  | Readonly<{ type: 'START_WITHOUT_SESSION'; message?: string }>
  | Readonly<{ type: 'LOGIN_STARTED'; username: string }>
  | Readonly<{ type: 'LOGIN_SUCCEEDED'; session: BrowserSession; village: VillageSummary }>
  | Readonly<{ type: 'TERRAIN_LOADED'; terrain: TerrainPayload }>
  | Readonly<{ type: 'TERRAIN_REPLACED'; terrain: TerrainPayload }>
  | Readonly<{ type: 'SESSION_ROTATED'; session: BrowserSession }>
  | Readonly<{ type: 'WORLD_LOAD_FAILED'; message: string; moduleLoadFailed?: boolean }>
  | Readonly<{ type: 'RETRY_WORLD' }>
  | Readonly<{ type: 'ACCOUNT_CREATION_FINISHED'; username: string; password: string }>
  | Readonly<{
      type: 'SESSION_ENDED';
      message?: string;
      loginRejection?: LegacyLoginRejection;
    }>
  | Readonly<{ type: 'FATAL_ERROR'; message: string }>;

export function reduceAppState(state: AppState, event: AppEvent): AppState {
  switch (event.type) {
    case 'START_WITHOUT_SESSION':
      return event.message === undefined
        ? { phase: 'anonymous' }
        : { phase: 'anonymous', message: event.message };
    case 'LOGIN_STARTED':
      return { phase: 'authenticating', username: event.username };
    case 'LOGIN_SUCCEEDED':
      return { phase: 'loading-world', session: event.session, village: event.village };
    case 'TERRAIN_LOADED':
      if (state.phase !== 'loading-world') return state;
      return {
        phase: 'world',
        session: state.session,
        village: state.village,
        terrain: event.terrain,
      };
    case 'TERRAIN_REPLACED':
      if (state.phase !== 'world' && state.phase !== 'world-load-failed') return state;
      return { ...state, terrain: event.terrain };
    case 'SESSION_ROTATED':
      if (
        state.phase !== 'world' &&
        state.phase !== 'loading-world' &&
        state.phase !== 'world-load-failed'
      ) {
        return state;
      }
      return { ...state, session: event.session };
    case 'WORLD_LOAD_FAILED':
      if (state.phase !== 'world') return state;
      return {
        phase: 'world-load-failed',
        session: state.session,
        village: state.village,
        terrain: state.terrain,
        message: event.message,
        assetRecoveryNonce: state.assetRecoveryNonce ?? 0,
        ...(event.moduleLoadFailed ? { moduleLoadFailed: true } : {}),
      };
    case 'RETRY_WORLD':
      if (state.phase !== 'world-load-failed') return state;
      return {
        phase: 'world',
        session: state.session,
        village: state.village,
        terrain: state.terrain,
        assetRecoveryNonce: state.assetRecoveryNonce + 1,
      };
    case 'ACCOUNT_CREATION_FINISHED':
      return { phase: 'anonymous', username: event.username, password: event.password };
    case 'SESSION_ENDED':
      return event.message === undefined && event.loginRejection === undefined
        ? { phase: 'anonymous' }
        : {
            phase: 'anonymous',
            ...(event.message === undefined ? {} : { message: event.message }),
            ...(event.loginRejection === undefined ? {} : { loginRejection: event.loginRejection }),
          };
    case 'FATAL_ERROR':
      return { phase: 'fatal', message: event.message };
  }
}
